import { type MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type { InvoiceFormFieldValues } from 'modules/Invoices/InvoiceForm/InvoiceForm.types';
import { invoiceFormToData } from 'shared/serializer';
import { useGetConstants } from 'shared/utils/selectors';
import type { StoreState } from 'types/storeTypes';
import { CreditNoteCreateParamsSchema, type InvoiceResponse } from './invoice-schemas';
import { type ApiError, addActionNameToMutationResult, useLegacyAxiosInstance } from './utils';

type CreateCreditNoteVariables = {
  data: InvoiceFormFieldValues;
};

export async function createCreditNoteFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateCreditNoteVariables,
  constants: StoreState['app']['constants'],
) {
  const instance = await axiosInstance;
  const serialized = invoiceFormToData(variables.data, constants);
  const validated = CreditNoteCreateParamsSchema.parse(serialized);
  return instance.post<InvoiceResponse>('/credit_notes', validated);
}

export function useCreateCreditNote(
  options: MutationOptions<
    AxiosResponse<InvoiceResponse>,
    ApiError,
    CreateCreditNoteVariables
  > = {},
) {
  const axiosInstance = useLegacyAxiosInstance();
  const constants = useGetConstants();
  const queryClient = useQueryClient();

  const mutation = useMutation<AxiosResponse<InvoiceResponse>, ApiError, CreateCreditNoteVariables>(
    {
      mutationFn: variables => createCreditNoteFn(axiosInstance, variables, constants),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['credit-notes'] });
      },
      ...options,
    },
  );

  return addActionNameToMutationResult<
    AxiosResponse<InvoiceResponse>,
    ApiError,
    CreateCreditNoteVariables,
    'createCreditNote'
  >('createCreditNote', mutation);
}
